// No automatic replay of telephony commands: a lost reply does not imply a lost action.
export const TAB_RPC_VERSION = 6;
export const OWNER_UNRESPONSIVE_MESSAGE = "Активная вкладка софтфона не ответила. Перейдите во вкладку, где подключён софтфон, и проверьте состояние звонка";
const ACK_MS = 10_000;
const RESULT_MS = 45_000;
const CACHE_MS = 60_000;
const STATUS_MS = 5_000;
type Message = Record<string, any>;
type Clock = { now(): number; monotonicNow?(): number; setTimeout(fn: () => void, ms: number): any; clearTimeout(id: any): void };
type RpcPhase = "requested" | "acknowledged" | "status_requested" | "status_unknown" | "succeeded" | "failed" | "ack_timeout" | "result_timeout" | "reset" | "send_failed";
type RpcObservation = { requestId: string; ownerTabId: string; command: string; rpcPhase: RpcPhase; elapsedMs: number };

export class TabRpc {
  private pending = new Map<string, any>();
  private received = new Map<string, any>();
  private generation = 0;
  constructor(private options: {
    self: string;
    send(kind: string, detail: Message): void;
    execute(command: string, args: any[], signal: AbortSignal, requester?: string): Promise<any>;
    clock?: Clock;
    observe?(detail: RpcObservation): void;
  }) {}
  private get clock(): Clock { return this.options.clock || {
    now: () => Date.now(), setTimeout: (fn, ms) => setTimeout(fn, ms), clearTimeout: (id) => clearTimeout(id)
  }; }
  private monotonicNow(): number { return this.options.clock?.monotonicNow?.() ?? performance.now(); }
  private observe(requestId: string, entry: any, rpcPhase: RpcPhase) {
    try {
      this.options.observe?.({ requestId, ownerTabId: entry.owner, command: entry.command, rpcPhase,
        elapsedMs: Math.max(0, Math.round(this.monotonicNow() - entry.startedAt)) });
    } catch { /* Observation must never affect command execution or cleanup. */ }
  }

  request(owner: string, command: string, args: any[] = []): Promise<any> {
    if (this.pending.size >= 32) return Promise.reject(new Error("Слишком много ожидающих команд софтфона"));
    const requestId = crypto.randomUUID();
    const issuedAt = this.clock.now();
    return new Promise((resolve, reject) => {
      const entry: any = { owner, command, resolve, reject, startedAt: this.monotonicNow() };
      entry.ack = () => {
        if (entry.acknowledged) return;
        entry.acknowledged = true;
        this.observe(requestId, entry, "acknowledged");
        this.clock.clearTimeout(entry.timer);
        entry.timer = this.clock.setTimeout(() => this.cancelPending(requestId,
          "Результат команды не подтверждён. Проверьте звонок в активной вкладке софтфона, прежде чем повторять действие", "result_timeout"),
          Math.max(0, RESULT_MS - (this.monotonicNow() - entry.startedAt)));
      };
      entry.timer = this.clock.setTimeout(() => this.cancelPending(requestId,
        OWNER_UNRESPONSIVE_MESSAGE, "ack_timeout"), ACK_MS);
      this.pending.set(requestId, entry);
      this.observe(requestId, entry, "requested");
      try {
        this.options.send("command", { requestId, target: owner, command, args, protocol: TAB_RPC_VERSION,
          issuedAt, acceptUntil: issuedAt + ACK_MS, deadline: issuedAt + RESULT_MS });
        this.scheduleStatus(requestId, entry);
      } catch (error) {
        this.pending.delete(requestId);
        this.clock.clearTimeout(entry.timer);
        this.observe(requestId, entry, "send_failed");
        reject(error);
      }
    });
  }

  handle(message: Message, owner: boolean): boolean {
    if (!["command", "command-ack", "command-result", "command-cancel", "command-status", "command-status-unknown"].includes(message.kind)) return false;
    if (message.target !== this.options.self) {
      // Old tabs may still hold the same registration lease after a runtime upgrade.
      if (owner && message.kind === "command" && !message.target) this.reply(message, false, "Обновите все вкладки DoCRM: версия софтфона изменилась");
      return true;
    }
    if (["command-ack", "command-result", "command-status-unknown"].includes(message.kind)) {
      const pending = this.pending.get(message.requestId);
      if (!pending || pending.owner !== message.sender) return true;
      if (message.kind === "command-status-unknown") {
        if (!pending.statusUnknown) this.observe(message.requestId, pending, "status_unknown");
        pending.statusUnknown = true;
        return true; // Never reinterpret "unknown" as permission to replay.
      }
      if (message.kind === "command-ack") { pending.ack(); return true; }
      this.pending.delete(message.requestId);
      this.clock.clearTimeout(pending.timer);
      this.clock.clearTimeout(pending.statusTimer);
      this.observe(message.requestId, pending, message.ok ? "succeeded" : "failed");
      if (message.ok) pending.resolve(message.result);
      else pending.reject(new Error(message.error || "Команда софтфона завершилась ошибкой"));
      return true;
    }
    const now = this.clock.now();
    for (const [id, value] of this.received) if (value.reply && value.until <= now) this.received.delete(id);
    const key = `${message.sender}:${message.requestId}`;
    const existing = this.received.get(key);
    if (message.kind === "command-cancel") { existing?.controller.abort(); return true; }
    if (message.kind === "command-status") {
      if (message.protocol !== TAB_RPC_VERSION) return true;
      if (existing?.reply) this.sendBestEffort("command-result", existing.reply);
      else if (owner || existing) this.sendBestEffort(existing ? "command-ack" : "command-status-unknown",
        { requestId: message.requestId, target: message.sender });
      return true; // Read-only: no new entry, execution, timer or lease change.
    }
    if (!owner) return true;
    if (existing) {
      if (existing.reply) this.sendBestEffort("command-result", existing.reply);
      else this.sendBestEffort("command-ack", { requestId: message.requestId, target: message.sender });
      return true;
    }
    if (message.protocol !== TAB_RPC_VERSION || !Number.isFinite(message.issuedAt) ||
        message.issuedAt > now + 1000 || message.acceptUntil !== message.issuedAt + ACK_MS ||
        message.deadline !== message.issuedAt + RESULT_MS || now >= message.acceptUntil) {
      this.reply(message, false, "Команда устарела. Повторите действие в активной вкладке");
      return true;
    }
    if (this.received.size >= 256) { this.reply(message, false, "Слишком много команд софтфона"); return true; }
    const entry: any = { controller: new AbortController(), until: now + CACHE_MS };
    const generation = this.generation;
    this.received.set(key, entry);
    entry.timer = this.clock.setTimeout(() => entry.controller.abort(), message.deadline - now);
    this.sendBestEffort("command-ack", { requestId: message.requestId, target: message.sender });
    const aborted = new Promise((_, reject) => entry.controller.signal.addEventListener("abort",
      () => reject(new Error("Выполнение команды отменено или истекло время ожидания")), { once: true }));
    Promise.race([aborted, Promise.resolve().then(() => {
      entry.controller.signal.throwIfAborted();
      return this.options.execute(message.command, Array.isArray(message.args) ? message.args : [], entry.controller.signal, message.sender);
    })]).then((result) => finish(true, result), (error) => finish(false, error?.message || "Ошибка команды"));
    const finish = (ok: boolean, value: any) => {
      this.clock.clearTimeout(entry.timer);
      if (generation !== this.generation) return;
      entry.until = this.clock.now() + CACHE_MS;
      // Commit the outcome before delivery; a failed send must not lose it.
      entry.reply = this.resultMessage(message, ok, value);
      this.sendBestEffort("command-result", entry.reply);
    };
    return true;
  }

  private resultMessage(message: Message, ok: boolean, value: any) {
    return { requestId: message.requestId, target: message.sender, ok, ...(ok ? { result: value } : { error: value }) };
  }
  private reply(message: Message, ok: boolean, value: any) {
    const reply = this.resultMessage(message, ok, value);
    this.sendBestEffort("command-result", reply);
    return reply;
  }
  private sendBestEffort(kind: string, detail: Message) {
    try { this.options.send(kind, detail); } catch { /* Read-only recovery or the original deadline handles loss. */ }
  }
  private scheduleStatus(requestId: string, entry: any) {
    if (this.pending.get(requestId) !== entry || (entry.statusCount || 0) >= 8) return;
    entry.statusTimer = this.clock.setTimeout(() => {
      if (this.pending.get(requestId) !== entry) return;
      entry.statusCount = (entry.statusCount || 0) + 1;
      this.observe(requestId, entry, "status_requested");
      this.sendBestEffort("command-status", { requestId, target: entry.owner, protocol: TAB_RPC_VERSION });
      this.scheduleStatus(requestId, entry);
    }, STATUS_MS);
  }

  private cancelPending(requestId: string, reason: string, rpcPhase: RpcPhase = "reset") {
    const entry = this.pending.get(requestId);
    if (!entry) return;
    this.pending.delete(requestId);
    this.clock.clearTimeout(entry.timer);
    this.clock.clearTimeout(entry.statusTimer);
    this.observe(requestId, entry, rpcPhase);
    try {
      this.options.send("command-cancel", { requestId, target: entry.owner });
    } catch {
      // Cancellation is best effort. A closed channel must not leave the caller
      // pending or interrupt cleanup; it also cannot prove the action stopped.
    }
    entry.reject(new Error(reason));
  }

  reset(reason = "Вкладка софтфона закрыта") {
    this.generation++;
    for (const requestId of this.pending.keys()) this.cancelPending(requestId, reason);
    for (const entry of this.received.values()) {
      this.clock.clearTimeout(entry.timer);
      entry.controller.abort();
    }
    this.received.clear();
  }
}
